import dayjs from 'dayjs'
import { useMemo } from 'react'
import { default as DP, ReactDatePickerProps } from 'react-datepicker'

import { DatepickerHeader } from './components/DatepickerHeader'
import { DatePickerInputField } from './components/DatepickerInput'
import { DatepickerInputTime } from './components/DatepickerInputTime'

const DATETIME_FORMAT = 'dd MMM yyyy HH:mm'
const DATE_FORMAT = 'dd MMM yyy'

type RDatePickerProps<WithRange extends boolean | undefined> = Omit<
  ReactDatePickerProps<never, WithRange>,
  'selected' | 'startDate' | 'endDate' | 'onChange' | 'minDate' | 'maxDate'
>

type DatePickerProps<WithRange extends boolean | undefined> = RDatePickerProps<WithRange> & {
  variant?: 'solid' | 'outline'
  selected?: string
  startDate?: string
  endDate?: string
  minDate?: string
  maxDate?: string
  onClear?: () => void
  onChange(
    date: WithRange extends false | undefined ? string | null : [string | null, string | null],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: React.SyntheticEvent<any> | undefined
  ): void
}

// date object initial = datetime (wib) + timezone (local)
const convertStringToDate = (date: string) => {
  const isDateWithTimezoneOffset = RegExp(/[+-]\d{2}(:|)\d{2}$/).test(date)
  const isUTCDate = RegExp(/Z$/).test(date)

  if (isUTCDate || isDateWithTimezoneOffset) {
    //'2024-01-01T08:00:00+07:00'
    //'2024-01-01T08:00:00+0700'
    //'2024-01-01T08:00:00-07:00'
    //'2024-01-15T03:53:10.908Z'
    return new Date(dayjs(date).tz().format('YYYY-MM-DD[T]HH:mm:ss'))
  } else {
    //'2024-01-01T08:00:00'
    //'2024-01-01 08:00:00'
    //'2024-01-01'
    return dayjs(date).toDate()
  }
}

// kirim keserver = datetime (local) + timzezone (wib)
const convertLocalToPreference = (date: Date, withTime: boolean) =>
  withTime ? dayjs(date).format('YYYY-MM-DD[T]HH:mm:ss') + dayjs().tz().format('Z') : dayjs(date).format('YYYY-MM-DD')

//'2024-01-01T08:00:00+07:00' -> new Date('2024-01-01T08:00:00+07:00') -> 2024-01-01T10:00:00+09:00
//2024-01-01T08:00:00+7 -> new Date(2024-01-01T08:00:00+7) -> 2024-01-01T10:00:00+09:00

const DatePicker = <WithRange extends boolean | undefined = undefined>({
  variant,
  isClearable,
  selected,
  startDate,
  endDate,
  minDate,
  maxDate,
  showTimeInput,
  onClear,
  onChange,
  ...props
}: DatePickerProps<WithRange>) => {
  const withTime = showTimeInput === true

  const convertedSelectedDate = useMemo(() => (selected ? convertStringToDate(selected) : undefined), [selected])
  const convertedStartDate = useMemo(() => (startDate ? convertStringToDate(startDate) : undefined), [startDate])
  const convertedEndDate = useMemo(() => (endDate ? convertStringToDate(endDate) : undefined), [endDate])
  const convertedMinDate = useMemo(() => (minDate ? convertStringToDate(minDate) : undefined), [minDate])
  const convertedMaxDate = useMemo(() => (maxDate ? convertStringToDate(maxDate) : undefined), [maxDate])

  const handleChange: ReactDatePickerProps<never, WithRange>['onChange'] = (date, e) => {
    if (Array.isArray(date)) {
      const castedOnChange = onChange as DatePickerProps<true>['onChange']
      const sDate = date[0] ? convertLocalToPreference(date[0], withTime) : null
      const eDate = date[1] ? convertLocalToPreference(date[1], withTime) : null
      castedOnChange([sDate, eDate], e)
    } else {
      const castedOnChange = onChange as DatePickerProps<false>['onChange']
      castedOnChange(date ? convertLocalToPreference(date, withTime) : null, e)
    }
  }

  return (
    <DP<never, WithRange>
      dateFormat={withTime ? DATETIME_FORMAT : DATE_FORMAT}
      useWeekdaysShort
      showPopperArrow={false}
      selected={convertedSelectedDate}
      startDate={convertedStartDate}
      endDate={convertedEndDate}
      minDate={convertedMinDate}
      maxDate={convertedMaxDate}
      popperProps={{ strategy: 'fixed' }}
      renderCustomHeader={(headerProps) => <DatepickerHeader {...headerProps} />}
      customTimeInput={<DatepickerInputTime />}
      customInput={
        <DatePickerInputField
          onClear={onClear}
          variant={variant}
          isClearable={isClearable}
        />
      }
      onChange={handleChange}
      showTimeInput={showTimeInput}
      {...props}
    />
  )
}

export type { DatePickerProps }

export { DatePicker }
